// pages/project/report.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import PaginatedListTable from '../../components/PaginatedListTable';
import ImageViewerModal from '../../components/ImageViewerModal';
import EngagementChart from '../../components/EngagementChart';
import ViewEngagementModal from '../../components/ViewEngagementModal';
import Container from '../../layouts/Container';

import api from '../../services/api';
import { getToday } from '../../utils/dateUtil';
import { useProjects } from '../../context/ProjectContext';
import { useUsers } from '../../context/UserContext';

import { getEngagementsByFormGroup } from '../../services/project/formGroupService'; // updated signature below
import { deleteEngagement, getReports } from '../../services/project/engagementService';
import { downloadEngagements } from '../../services/project/reportService';
import AnchorTag from '../../components/AnchorTag';
import { XOctagon } from 'react-bootstrap-icons';

function Reports() {
  const tableRef = useRef();
  const { projectId } = useParams();
  const { projects, isProjectReadOnly } = useProjects();
  const { isSuperAdmin } = useUsers();

  const [startDate, setStartDate] = useState(getToday());
  const [endDate, setEndDate] = useState(getToday());
  const [formGroups, setFormGroups] = useState([]);
  const [selectedFormGroupId, setSelectedFormGroupId] = useState(null);

  const [forms, setForms] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [engagement, setEngagement] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [loadingFG, setLoadingFG] = useState(true);
  const [loadingEngagements, setLoadingEngagements] = useState(false);
  const [reports, setReports] = useState([]);

  const currentProject = projects.find(p => p.id === parseInt(projectId, 10));

  useEffect(() => {
    const fetchReports = async () => {
      if (!selectedFormGroupId) return;
      setLoadingEngagements(true);
      try {
        const res = await getReports(projectId, selectedFormGroupId);
        setReports(res || []);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load reports');
      } finally {
        setLoadingEngagements(false);
      }
    };
    fetchReports();
  }, [selectedFormGroupId]);
  useEffect(() => {
    const init = async () => {
      try {
        const res = await api.get(`/projects/${projectId}/formgroups`);
        setFormGroups(res.data || []);
        const activeGroup = (res.data || []).find(g => g.isActive) || (res.data || [])[0];
        if (activeGroup) {
          const fgId = String(activeGroup.id);
          setSelectedFormGroupId(fgId);
          await fetchForms(fgId);
          // initial table fetch happens automatically on first render via PaginatedListTable
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to load form groups');
      } finally {
        setLoadingFG(false);
      }
    };
    init();
  }, [projectId]);

  const fetchForms = async (formGroupId) => {
    try {
      const res = await api.get(`/projects/${projectId}/formgroups/${formGroupId}`);
      setForms(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load forms');
    }
  };

  const handleApply = async () => {
    // refresh fields (in case group changed) then refetch the table
    if (selectedFormGroupId) await fetchForms(selectedFormGroupId);
    tableRef.current?.refetch(); // reset to first page on apply
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      await downloadEngagements(
        {
          formGroupId: selectedFormGroupId,
          startDate: new Date(`${startDate}T00:00:00+08:00`).toISOString(),
          endDate: new Date(`${endDate}T23:59:59+08:00`).toISOString()
        }
      );
      toast.success('Report generation started');
      // Optionally refresh the reports list to show the new queued report
      tableRef.current?.refetch();
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to start report generation');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDelete = async (engagementId) => {
    if (!window.confirm('Are you sure you want to delete this engagement?')) return;
    try {
      const response = await deleteEngagement(engagementId);
      if (!response.success) {
        toast.error(response.data?.error || 'Delete failed');
        return;
      }
      tableRef.current?.refetch(); // refresh current page
      toast.success(response.message || 'Deleted');
    } catch (error) {
      console.error('Error deleting engagement:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const viewEngagement = (row) => {
    setEngagement(row);
    setShowModal(true);
  };

  const handleImageClick = (imageSrc, footer) => {
    setSelectedImage({
      src: imageSrc,
      footer
    });
    setShowImageModal(true);
  };

  const columnHelper = createColumnHelper();

  const fieldColumns = useMemo(() => {
    const formWithFields = forms.find(f => Array.isArray(f.fields) && f.fields.length > 0);
    if (!formWithFields) return [];
    return formWithFields.fields
      .filter(field => field.type !== 'label' && field.type !== 'address')
      .slice(0, 4)
      .map(field =>
        columnHelper.accessor(
          row => {
            const value = row.fields?.[field.name];
            return value;
          },
          {
            id: field.name,
            header: field.label || field.name,
            cell: info => {
              const value = info.getValue();
              if (field.type === 'image') {
                return value ? (
                  <img
                    src={value?.uri?.[0]}
                    alt=""
                    style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
                  />
                ) : 'No image';
              }
              return value;
            },
          }
        )
      );
  }, [forms]);

  const detailColumns = [
    columnHelper.accessor(
      row => `${row.User?.firstName || ''} ${row.User?.lastName || ''}`.trim(),
      {
        id: 'fullName',
        header: 'Reported By',
      }
    ),
    columnHelper.accessor(row => row.createdAt, {
      id: 'createdAt',
      header: 'Synced Date',
      cell: info => new Date(info.getValue()).toLocaleString(),
    }),
    columnHelper.accessor(row => row.submittedAt, {
      id: 'submittedAt',
      header: 'Submitted Date',
      cell: info => info.getValue() ? new Date(info.getValue()).toLocaleString() : '—',
    }),
    currentProject?.settings?.approval && columnHelper.accessor(
      row => row.approverName,
      {
        id: 'approver',
        header: 'Approver',
        cell: info => info.getValue() || '—',
      }
    ),
    (!isProjectReadOnly && isSuperAdmin) && {
      id: 'actions',
      header: 'Actions',
      meta: { sticky: true },
      cell: ({ row }) => (
        <AnchorTag onClick={() => handleDelete(row.original.id)} hoverText="Delete">
          <XOctagon />
        </AnchorTag>
      ),
    },
  ].filter(Boolean);

  // This is the single source of truth for fetching paginated rows (just like Stores)
  const fetchPaginatedEngagements = async (params) => {
    setLoadingEngagements(true);
    try {
      const startDateUTC = new Date(`${startDate}T00:00:00+08:00`).toISOString();
      const endDateUTC = new Date(`${endDate}T23:59:59+08:00`).toISOString();

      // Only on reports page: omit status if approval or validation is not false
      let queryParams = {
        formGroupId: selectedFormGroupId,
        startDate: startDateUTC,
        endDate: endDateUTC,
        ...params,
      };
      if (
        currentProject &&
        (currentProject.settings?.approval !== false || currentProject.settings?.validation !== false)
      ) {
        // Remove status from query if present
        queryParams = {
          ...queryParams,
          omitStatus: true,
        }
      }

      const res = await getEngagementsByFormGroup(projectId, queryParams);
      return res;
    } catch (err) {
      console.error(err);
      toast.error('Error fetching engagements.');
      return { data: [], totalPages: 0 };
    } finally {
      setLoadingEngagements(false);
    }
  };

  return (
    <Container>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <h2 className="mb-0">Reports</h2>
        <EngagementChart reports={reports} />
        <Form className="d-flex align-items-center gap-3 flex-wrap mb-0">
          <Form.Group className="d-flex align-items-center gap-2 mb-0">
            <Form.Label className="mb-0">Start</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="d-flex align-items-center gap-2 mb-0">
            <Form.Label className="mb-0">End</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="d-flex align-items-center gap-2 mb-0">
            <Form.Label className="mb-0" style={{ whiteSpace: 'nowrap' }}>
              Form Group
            </Form.Label>
            <Form.Select
              value={selectedFormGroupId || ''}
              onChange={async e => {
                const fgId = e.target.value;
                setSelectedFormGroupId(fgId);
                await fetchForms(fgId);
                tableRef.current?.refetch({ page: 0 });
              }}
              disabled={loadingFG || !formGroups.length}
            >
              {(formGroups || []).map(g => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Button variant="primary" type="button" onClick={handleApply}>
            Apply
          </Button>
          <Button variant="success" disabled={isDownloading} onClick={handleDownload}>
            {isDownloading ? <Spinner size="sm" /> : 'Export'}
          </Button>
        </Form>
      </div>

      {loadingFG ? (
        <p>Loading...</p>
      ) : (
        <>
          <PaginatedListTable
            ref={tableRef}
            tableId="engagement-list"
            columns={[...fieldColumns, ...detailColumns]}
            rowIdAccessor={row => row.id}
            onRowClick={viewEngagement}
            onFetchData={fetchPaginatedEngagements}
            // Optional: set defaultSort to match backend default
            defaultSort={[['submittedAt', 'DESC']]}
            isLoading={loadingEngagements}
          />

          <ViewEngagementModal
            show={showModal}
            forms={forms}
            engagement={engagement}
            handleImageClick={handleImageClick}
            onClose={() => setShowModal(false)}
          />

          {showImageModal && selectedImage && (
            <ImageViewerModal
              show={showImageModal}
              imageUrl={selectedImage.src}
              footer={selectedImage.footer}
              onClose={() => setShowImageModal(false)}
            />
          )}
        </>
      )}
    </Container>
  );
}

export default Reports;
